/**
 * The terms being typed in an action modal, as the backend's action input
 * (SE-8458).
 *
 * `planningModeStore`'s own `toGqlAction` already maps a STORED draft detail
 * onto `ScenarioActionInput`, and `getDraftPayload` maps the whole staged set.
 * Neither can serve this: the Live Impact verdict must describe the scenario
 * INCLUDING the terms currently in the open modal, which are not in the store
 * until Confirm. This module maps those in-flight terms, and
 * {@link mergeEditedAction} splices them over the staged payload.
 *
 * The field mapping is deliberately identical to `toGqlAction`'s — `signingFee`
 * carries the transfer fee, `wage` carries the annual salary, and
 * `coveredSharePercent` carries the loan's covered share. If the two ever
 * disagree, the panel's verdict and the scenario the user actually saves would
 * be computed from different deals, which is the whole failure mode SE-8458 is
 * about. `actionTermsToScenarioInput.test.ts` pins them together.
 * @module features/squad-planning/utils/actionTermsToScenarioInput
 */

import { TransitionType } from "@/generated/graphql";

import type { ActionTerms } from "@/features/financial-compliance/domain/actionFinancials";
import type { ScenarioActionInput } from "@/generated/graphql";

/**
 * Squad Planning's four actions, as backend transitions.
 *
 * `Loan` is `LOAN_OUT`: Squad Planning only ever loans a player away, and the
 * borrowing direction (`LOAN_IN`) is not reachable from these modals. This
 * mirrors `planningModeStore`'s `ACTION_TO_TRANSITION`.
 */
const TRANSITION: Readonly<Record<ActionTerms["kind"], TransitionType>> = {
  Sign: TransitionType.Sign,
  Sell: TransitionType.Sell,
  Loan: TransitionType.LoanOut,
  Promote: TransitionType.Promote,
};

/**
 * The financial details for one set of in-flight terms.
 *
 * Every branch writes only the fields its own action has. A `null` travels as
 * `null` rather than being dropped or substituted — the compliance backend
 * reports PARTIAL for an absent covered share rather than assuming 100
 * (SE-7411), and that is the honest answer for terms the user has not finished
 * typing. Inventing a zero here would turn "not entered yet" into "entered as
 * nothing".
 * @param terms - The terms currently in the modal.
 * @returns The financial details for those terms.
 */
const financialDetailsFor = (
  terms: ActionTerms
): NonNullable<ScenarioActionInput["financialDetails"]> => {
  switch (terms.kind) {
    case "Sign":
      return {
        signingFee: terms.transferFee,
        wage: terms.annualSalary,
        durationMonths: terms.durationMonths,
        signingBonus: terms.signingBonus,
        agentFee: terms.agentFee,
      };
    case "Sell":
      return { salePrice: terms.salePrice };
    case "Loan":
      return {
        loanFee: terms.loanFee,
        durationMonths: terms.durationMonths,
        coveredSharePercent: terms.wageSharePercent,
      };
    case "Promote":
      return {
        wage: terms.annualSalary,
        durationMonths: terms.durationMonths,
      };
  }
};

/**
 * Map the modal's in-flight terms onto one backend action input.
 * @param playerId - The player being acted on; empty for an external sign.
 * @param terms - The terms currently in the modal.
 * @returns The action input.
 */
export const actionTermsToScenarioInput = (
  playerId: string,
  terms: ActionTerms
): ScenarioActionInput => ({
  playerId,
  transitionType: TRANSITION[terms.kind],
  financialDetails: financialDetailsFor(terms),
});

/**
 * Splice the action being edited over the scenario's staged payload.
 *
 * The edited action SUPERSEDES its own staged copy rather than joining it —
 * counting both would double the player and report an impact the scenario never
 * has. That is SE-7002's rule, which `useScenarioImpact` already applies to the
 * client-side delta arithmetic; this applies the same rule to what the SERVER
 * is asked to evaluate, so the two halves of the panel describe one scenario.
 *
 * `externalPlayerData` is carried over from the staged entry when there is one.
 * An external sign has no player record, so that block is the only thing
 * identifying who the deal is for, and rebuilding the entry from terms alone
 * would drop it — the server would then evaluate a signing of nobody.
 * @param staged - The scenario's staged actions, from the planning store.
 * @param playerId - The player being edited; empty for an external sign.
 * @param terms - The terms currently in the modal.
 * @returns The staged actions with the edited one applied.
 */
export const mergeEditedAction = (
  staged: readonly ScenarioActionInput[],
  playerId: string,
  terms: ActionTerms
): readonly ScenarioActionInput[] => {
  const edited = actionTermsToScenarioInput(playerId, terms);
  const previous = staged.find(action => (action.playerId ?? "") === playerId);

  const replacement: ScenarioActionInput =
    previous?.externalPlayerData == null
      ? edited
      : { ...edited, externalPlayerData: previous.externalPlayerData };

  if (previous == null) return [...staged, replacement];
  return staged.map(action =>
    (action.playerId ?? "") === playerId ? replacement : action
  );
};
